//#region Imports

import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { MessagePayload } from '../../models/payloads/message.payload';
import { CrudRequestResponseProxy } from '../../models/proxies/crud-request-response.proxy';
import { LessonMediaProxy } from '../../models/proxies/lesson-media.proxy';
import { LessonRatingProxy } from '../../models/proxies/lesson-rating.proxy';
import { LessonProxy } from '../../models/proxies/lesson.proxy';
import { MessageProxy } from '../../models/proxies/message.proxy';
import { TranscriptionTokenProxy } from '../../models/proxies/transcription-token.proxy';
import { UserProgressProxy } from '../../models/proxies/user-progress.proxy';
import { AsyncResult } from '../../modules/http-async/models/async-result';
import { HttpAsyncService } from '../../modules/http-async/services/http-async.service';
import { isUuid } from "../../utils/functions";
import { createCrudUrl, CrudRequestParams } from '../../utils/crud';
import { GetMany } from '../../models/proxies/get-many.proxy';

//#endregion

type ManyResponseAsync<T> = Promise<AsyncResult<CrudRequestResponseProxy<T>>>;

@Injectable({
  providedIn: 'root',
})
export class LessonInteractor {

  //#region Constructors

  constructor(
    private readonly http: HttpAsyncService,
  ) {}

  //#endregion

  //#region Functions

  public async getProgressBySubject(userId: string, courseId: string): ManyResponseAsync<UserProgressProxy> {
    const isUUid: boolean = isUuid(courseId);

    const searchParams = encodeURIComponent(JSON.stringify({
      userId,
      ...isUUid && { courseId },
      ...!isUUid && { 'course.slug': courseId },
      isActive: true,
      "$or":  [
        { "quiz.isActive": true },
        { "lesson.isActive": true }
      ],
    }));

    const url = environment.routes.userLessonProgress
      .replace('{searchParams}', searchParams)  + `&join[0]=lesson&join[1]=quiz`;

    return await this.http.get(url);
  }

  public async getCourseProgressPercentage(userId: string, courseId: string): Promise<AsyncResult<number>> {

    const url = environment.api.userProgress.coursePercentage
      .replace('{courseId}', courseId)
      .replace('{userId}', userId);

    return await this.http.get(url);
  }

  public async getLessonProgressByLesson(userId: string, lessonId: string): ManyResponseAsync<UserProgressProxy> {
    const searchParams = encodeURIComponent(JSON.stringify({
      userId,
      lessonId,
      isActive: true,
    }));

    const url = environment.routes.userLessonProgress
      .replace('{searchParams}', searchParams);

    return await this.http.get<CrudRequestResponseProxy<UserProgressProxy>>(url);
  }

  public async getUserProgressByManyLessons(userId: string, lessonsIds: string[]): ManyResponseAsync<UserProgressProxy> {
    const searchParams = encodeURIComponent(JSON.stringify({
      userId,
      lessonId: { $in : lessonsIds },
      isActive: true,
    }));

    const url = environment.routes.userLessonProgress
      .replace('{searchParams}', searchParams);

    return await this.http.get<CrudRequestResponseProxy<UserProgressProxy>>(url);
  }

  public async getUserProgressByQuiz(
    userId: string,
    quizId: string
  ): ManyResponseAsync<UserProgressProxy> {
    const searchParams = encodeURIComponent(JSON.stringify({
      userId,
      quizId,
      isActive: true,
    }));

    const url = environment.routes.userLessonProgress
      .replace('{searchParams}', searchParams);

    return await this.http.get<CrudRequestResponseProxy<UserProgressProxy>>(url);
  }

  public async getUserProgressByManyQuizzes(
    userId: string,
    quizzesIds: string[]
  ): ManyResponseAsync<UserProgressProxy> {
    const searchParams = encodeURIComponent(JSON.stringify({
      userId,
      quizId: { $in : quizzesIds },
      isActive: true,
    }));

    const url = environment.routes.userLessonProgress
      .replace('{searchParams}', searchParams);

    return await this.http.get<CrudRequestResponseProxy<UserProgressProxy>>(url);
  }

  public async markLessonProgress(lessonId: string): Promise<AsyncResult<void>> {
    return await this.http.post(`${ environment.routes.markProgress }/${ lessonId }/mark`, {});
  }

  public async updateLessonVideoTime(lessonId: string, videoTime: string): Promise<AsyncResult<UserProgressProxy>> {
    const url = environment.routes.userLessonProgressVideoTime
      .replace('{lessonId}', lessonId)
      .replace('{videoTime}', videoTime);

    return await this.http.put<UserProgressProxy>(url, {});
  }

  public async getLessonsRatingsBySubject(userId: string, courseId: string): ManyResponseAsync<LessonRatingProxy> {
    const searchParams = encodeURIComponent(JSON.stringify({
      userId,
      courseId,
      isActive: true,
    }));

    const url = environment.routes.userLessonRatings
      .replace('{searchParams}', searchParams);

    return await this.http.get(url);
  }

  public async getMany(crudParams: CrudRequestParams<LessonProxy>): Promise<AsyncResult<GetMany<LessonProxy>>> {
    let url = createCrudUrl(environment.routes.lesson, crudParams);
    return await this.http.get<GetMany<LessonProxy>>(url);
  }

  public async getOneLesson(lessonId: string, includeMedia: boolean = true): Promise<AsyncResult<LessonProxy>> {
    let url = environment.routes.getLessonWithActiveMedias.replace('{lessonId}', lessonId);

    if (includeMedia)
      url += '&join[0]=mediaLessons';

    return await this.http.get<LessonProxy>(url);
  }

  public async getCommentByLesson(lessonId: string): ManyResponseAsync<MessageProxy> {
    const searchParams = encodeURIComponent(JSON.stringify({
      lessonId,
      isActive: true,
    }));

    const url = environment.routes.messages
      .replace('{searchParams}', searchParams);

    return await this.http.get<CrudRequestResponseProxy<MessageProxy>>(url);
  }

  public async updateComment(message: MessagePayload, lessonId: string, messageId: string): ManyResponseAsync<string> {
    const url = environment.routes.updateMessage
      .replace('{lessonId}', lessonId)
      .replace('{messageId}', messageId);

    return await this.http.put<CrudRequestResponseProxy<string>>(url, {
      isActive: true,
      ...message,
    });
  }

  public async deleteCommentById(lessonId: string, messageId: string): ManyResponseAsync<string> {
    const url = environment.routes.deleteMessage
      .replace('{lessonId}', lessonId)
      .replace('{messageId}', messageId);

    return await this.http.delete<CrudRequestResponseProxy<string>>(url);
  }

  public async makeCommentOnLesson(lessonId: string, comment: MessagePayload): Promise<AsyncResult<void>> {
    return await this.http.post(`${ environment.routes.lesson }/${ lessonId }/messages`, comment);
  }

  public async getMediasByLessonId(): Promise<AsyncResult<LessonMediaProxy[] | CrudRequestResponseProxy<LessonMediaProxy>>> {
    const searchParams = encodeURIComponent(JSON.stringify({
      isActive: true,
    }));

    const url = environment.api.medias.get.replace('{searchParams}', searchParams);

    return await this.http.get<LessonMediaProxy[] | CrudRequestResponseProxy<LessonMediaProxy>>(url);
  }

  public async getTranscriptionTokensByLessonId(lessonId: string): Promise<AsyncResult<TranscriptionTokenProxy[]>> {
    const url = environment.api.lessons.getTranscriptionToken
      .replace('{lessonId}', lessonId);

    return await this.http.get<TranscriptionTokenProxy[]>(url);
  }

  public async getModuleFirstLesson(moduleId: string): ManyResponseAsync<LessonProxy> {
    const searchParams = encodeURIComponent(JSON.stringify({
      moduleId,
      isActive: true,
    }));

    const url = environment.routes.lesson + `?s=${searchParams}&sort=order,ASC`;

    return await this.http.get<CrudRequestResponseProxy<LessonProxy>>(url);
  }

  public async resetModuleProgress(moduleId: string): ManyResponseAsync<LessonProxy> {
    const url = environment.routes.resetModuleProgress.replace('{moduleId}', moduleId);

    return await this.http.delete<CrudRequestResponseProxy<LessonProxy>>(url);
  }

  public async checkCurrentModuleProgress(moduleId: string): Promise<AsyncResult<boolean>> {
    const url = environment.routes.checkModuleProgress.replace('{moduleId}', moduleId);

    return await this.http.get<boolean>(url);
  }

  public async checkCourseCompleted(courseId: string): Promise<AsyncResult<boolean>> {
    const url = environment.routes.checkCourseCompleted.replace('{courseId}', courseId);

    return await this.http.get<boolean>(url);
  }

  //#endregion
}
